import { startIntegrationOAuth } from '@/lib/open-integration-oauth';
import { queryKeys } from '@/lib/query-keys';
import { trackEvent } from '@/lib/track-event';
import { useFlagStatus } from '@/lib/use-flag-status';
import { useRuntimeConfig } from '@borradh-workspace/runtime-config/client';
import { Capacitor } from '@capacitor/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useInitiateMetaAdsFlfb } from '../api/initiate-meta-ads-flfb';
import { openConnectHandoff } from '../lib/open-connect-handoff';
import { useFacebookSdk } from './use-facebook-sdk';

/**
 * A phone or tablet browser, where FB.login cannot work (see `launch`).
 *
 * The user-agent alone is not enough: Android Chrome in "Desktop site" mode,
 * and Chrome on most Android tablets by default, report as desktop Linux. That
 * is exactly how one customer kept failing after switching modes. A
 * touch-only primary pointer catches those, and leaves touchscreen laptops,
 * whose primary pointer still hovers, on the popup.
 */
export const isMobileBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) return true;
  return (
    window.matchMedia?.('(hover: none) and (pointer: coarse)').matches ?? false
  );
};

/**
 * Gates the phone redirect. Off (or not loaded yet) keeps the popup on every
 * browser — the behaviour before the redirect existed — so turning the flag off
 * is an instant rollback. Target an org's group to try it on one clinic first.
 */
export const META_MOBILE_REDIRECT_FLAG = 'rollout-meta-mobile-redirect';

interface UseMetaLoginForBusinessOptions {
  onSuccess?: (data: { integrationId: string }) => void;
  onError?: (error: Error) => void;
}

/**
 * Opens Meta's Facebook Login for Business popup — one config covering
 * Facebook Pages, ad accounts, and Instagram — and starts the Meta Ads
 * integration server-side when the user completes it. This is the FLFB
 * replacement for the classic redirect OAuth (useInitiateMetaAdsAuth).
 *
 * Flow:
 *   1. FB SDK loads + initializes in the background (useFacebookSdk).
 *   2. launch() calls FB.login with the FLFB `config_id`. The flow is
 *      redirect-less — the code is returned in-page via the SDK callback,
 *      so there is no postMessage/waba step like WhatsApp has.
 *   3. On success, POSTs { code } to /integrations/meta-ads/initiate, which
 *      exchanges it for a non-expiring system-user token and saves a
 *      pending_selection integration. onSuccess fires with the integrationId
 *      so the caller can navigate to the selection wizard (/connect/meta-ads).
 *
 * Phone and tablet browsers skip the popup for the same flow as a full-page
 * redirect (see `isMobileBrowser`); the popup's in-page code does not survive
 * Facebook taking over the tab.
 *
 * launch() must be called synchronously from a user gesture — do not await
 * anything before calling it or popup blockers will kill the window.
 */
export const useMetaLoginForBusiness = (
  options?: UseMetaLoginForBusinessOptions
) => {
  const isNative = Capacitor.isNativePlatform();
  const { fb, isReady } = useFacebookSdk();
  const [isLaunching, setIsLaunching] = useState(false);
  const { metaLoginConfigId: configId } = useRuntimeConfig();
  const queryClient = useQueryClient();
  const { enabled: mobileRedirectEnabled } = useFlagStatus(
    META_MOBILE_REDIRECT_FLAG
  );
  const redirectOnMobile = mobileRedirectEnabled && isMobileBrowser();

  const { initiateAsync, isInitiating } = useInitiateMetaAdsFlfb({
    onSuccess: (data) => {
      setIsLaunching(false);
      options?.onSuccess?.(data);
    },
    onError: (err) => {
      setIsLaunching(false);
      options?.onError?.(err);
    },
  });

  const launch = useCallback(() => {
    // Same constraint as WhatsApp Embedded Signup: FB.login cannot run from
    // the Capacitor WebView (its `https://localhost` origin is never on the
    // Meta app's JS-SDK allowed domains — "unknown host domain"). Hand the
    // flow to the system browser on the real app domain; /connect/facebook
    // runs the popup there. onSuccess chains (navigation, campaign launch)
    // cannot fire across the hand-off — the hand-off invalidates every
    // integrations query instead, so call sites re-derive the connected state.
    if (isNative) {
      void openConnectHandoff({
        path: '/connect/facebook',
        queryClient,
        queryKey: queryKeys.integrations.all(),
        providerLabel: 'Facebook',
      });
      return;
    }

    // FB.login hands the code to an in-memory callback. In a phone browser
    // Facebook takes over the tab instead of opening a window beside it, our
    // page comes back as a fresh document, and the callback — with the code —
    // is gone. No request ever reaches the API, so it fails without a trace.
    // Run the same FLfB configuration as a full-page redirect instead; it
    // returns through the API callback and lands back on integrations.
    // onSuccess cannot fire across the redirect, same as the native hand-off.
    if (redirectOnMobile) {
      trackEvent('integrations.meta_flfb_redirect_started');
      void startIntegrationOAuth('integrations/meta-ads/flfb-authorize-url');
      return;
    }

    if (!fb) {
      toast.error('Facebook is still loading — try again in a moment');
      return;
    }
    if (!configId) {
      toast.error('Facebook Login for Business is not configured');
      return;
    }

    setIsLaunching(true);

    // FB SDK rejects AsyncFunction callbacks — keep this sync and let the
    // mutation run detached. onSuccess / onError are wired on the mutation.
    fb.login(
      (response) => {
        const code = response?.authResponse?.code;
        if (!code) {
          setIsLaunching(false);
          // This branch used to be silent, which left a customer clicking
          // Connect a dozen times with nothing on screen and nothing in our
          // logs to say why. `status` separates a closed window ('unknown')
          // from a refusal ('not_authorized').
          trackEvent('integrations.meta_flfb_login_no_code', {
            status: response?.status ?? null,
            userAgent: navigator.userAgent,
          });
          toast.error(
            "Facebook didn't finish connecting. If no Facebook window opened, allow pop-ups for this site and try again."
          );
          return;
        }
        initiateAsync({ code }).catch(() => {
          /* surfaced via the mutation's onError toast */
        });
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
      }
    );
  }, [fb, configId, initiateAsync, isNative, queryClient, redirectOnMobile]);

  return {
    launch,
    // Native and phone browsers never need the FB SDK — the system browser
    // or the redirect does the work.
    isReady: isNative || redirectOnMobile || isReady,
    isBusy: isLaunching || isInitiating,
  };
};
